'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function MassCalculator() {
  const t = useTranslations("tools.massCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    density: 8900,
    densityUnit: 'kg/m3',
    volume: 1,
    volumeUnit: 'm3'
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const densityConversions: { [key: string]: number } = {
    'kg/m3': 1,
    'g/cm3': 1000,
    'g/m3': 0.001,
    'kg/cm3': 1000000,
    'g/mm3': 1000000,
    'mg/m3': 0.000001,
    'mg/cm3': 0.001,
    'kg/L': 1000,
    'g/L': 1,
    'mg/L': 0.001,
    'lb/in3': 27679.904710191,
    'lb/ft3': 16.018463373960,
    'lb/yd3': 0.59327642110908,
    'lb/gal': 119.82642730074,
    'oz/in3': 1729.9940443869,
    'oz/ft3': 1.0011539608725,
    'oz/gal': 7.4891517062962
  };

  const volumeConversions: { [key: string]: number } = {
    'm3': 1,
    'cm3': 0.000001,
    'mm3': 0.000000001,
    'L': 0.001,
    'mL': 0.000001,
    'ft3': 0.028316846592,
    'in3': 0.000016387064,
    'yd3': 0.764554857984,
    'gal': 0.003785411784,
    'qt': 0.000946352946,
    'pt': 0.000473176473,
    'km3': 1000000000,
    'mi3': 4168181825.440579584
  };

  const massUnits: { [key: string]: { name: string; factor: number } } = {
    'kg': { name: 'Kilograms', factor: 1 },
    'g': { name: 'Grams', factor: 1000 },
    'mg': { name: 'Milligrams', factor: 1000000 },
    't': { name: 'Metric Tons', factor: 0.001 },
    'lb': { name: 'Pounds', factor: 2.20462262185 },
    'oz': { name: 'Ounces', factor: 35.27396194958 },
    'ton': { name: 'US Tons', factor: 0.00110231131092 }
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const { density, densityUnit, volume, volumeUnit } = inputs;

    if (isNaN(density) || isNaN(volume)) {
      setError(t('errorInvalidNumbers'));
      setResults(null);
      return;
    }

    if (density <= 0 || volume <= 0) {
      setError(t('errorPositiveNumbers'));
      setResults(null);
      return;
    }

    const densityInKgM3 = density * densityConversions[densityUnit];
    const volumeInM3 = volume * volumeConversions[volumeUnit];
    const massInKg = densityInKgM3 * volumeInM3;

    const conversions: { [key: string]: string } = {};
    for (const [unit, data] of Object.entries(massUnits)) {
      const convertedValue = massInKg * data.factor;
      conversions[unit] = convertedValue.toExponential(4);
    }

    setResults({
      massInKg: massInKg.toFixed(6),
      conversions
    });
  };

  const handleClear = () => {
    setInputs({
      density: 8900,
      densityUnit: 'kg/m3',
      volume: 1,
      volumeUnit: 'm3'
    });
    setResults(null);
    setError('');
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: '0 0 1rem 0' },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: 0 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '900px',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const
    },
    {},
    {},
    {}
  );

  const toolHeaderStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: '2rem',
    textAlign: 'center' as const
  };

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#1e293b', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputWrapperStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const inputStyle = {
    flex: 1,
    padding: '0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease'
  };

  const selectStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '0.9rem',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    { flex: 1 },
    { flex: 1.2 },
    { flex: 1.2 }
  );

  const errorMessageStyle = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    borderLeft: '4px solid #dc2626',
    display: error ? 'block' : 'none'
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '2rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = {
    flex: 1,
    padding: '1rem',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '1.1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white'
  };

  const clearBtnStyle = {
    flex: 1,
    padding: '1rem',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '1.1rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    background: '#f1f5f9',
    color: '#475569'
  };

  const resultsStyle = {
    padding: '2rem',
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    borderTop: '3px solid #f59e0b',
    display: results ? 'block' : 'none'
  };

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = {
    display: 'grid',
    gap: '1rem'
  };

  const resultItemStyle = {
    background: 'white',
    padding: '1.25rem',
    borderRadius: '0.75rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
  };

  const resultPrimaryStyle = {
    ...resultItemStyle,
    background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    border: '2px solid #2563eb'
  };

  const resultLabelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#475569' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const resultPrimaryValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.5rem' },
    { fontSize: '2rem' },
    { fontSize: '2rem' }
  );

  const conversionTableStyle = {
    marginTop: '1.5rem',
    background: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden' as const,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
  };

  const conversionTableH4Style = getResponsiveStyle(
    { margin: 0, padding: '1rem', background: '#f8fafc', color: '#1e293b', borderBottom: '2px solid #e2e8f0' },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const conversionRowStyle = {
    padding: '0.75rem 1rem',
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: '1px solid #f1f5f9'
  };

  const conversionUnitStyle = getResponsiveStyle(
    { color: '#64748b' },
    { fontSize: '0.85rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const conversionValueStyle = {
    fontWeight: 600,
    color: '#1e293b'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const referenceListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0
  };

  const referenceItemStyle = {
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid rgba(255, 200, 180, 0.2)'
  };

  const referenceLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 600,
    transition: 'color 0.3s ease'
  };

  const referenceUrlStyle = {
    color: colors.textSecondary,
    fontSize: '0.9rem',
    wordBreak: 'break-all' as const,
    display: 'block',
    marginTop: '0.25rem'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>⚖️ {t('toolTitle')}</h2>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="density">{t('densityLabel')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    style={inputStyle}
                    type="number"
                    id="density"
                    step="any"
                    value={inputs.density}
                    onChange={(e) => setInputs({...inputs, density: parseFloat(e.target.value) || 0})}
                    required
                  />
                  <select
                    style={selectStyle}
                    id="density-unit"
                    value={inputs.densityUnit}
                    onChange={(e) => setInputs({...inputs, densityUnit: e.target.value})}
                  >
                    <option value="kg/m3">kg/m³</option>
                    <option value="g/cm3">g/cm³</option>
                    <option value="g/m3">g/m³</option>
                    <option value="kg/cm3">kg/cm³</option>
                    <option value="g/mm3">g/mm³</option>
                    <option value="mg/m3">mg/m³</option>
                    <option value="mg/cm3">mg/cm³</option>
                    <option value="kg/L">kg/L</option>
                    <option value="g/L">g/L</option>
                    <option value="mg/L">mg/L</option>
                    <option value="lb/in3">lb/in³</option>
                    <option value="lb/ft3">lb/ft³</option>
                    <option value="lb/yd3">lb/yd³</option>
                    <option value="lb/gal">lb/gal (US)</option>
                    <option value="oz/in3">oz/in³</option>
                    <option value="oz/ft3">oz/ft³</option>
                    <option value="oz/gal">oz/gal (US)</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="volume">{t('volumeLabel')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    style={inputStyle}
                    type="number"
                    id="volume"
                    step="any"
                    value={inputs.volume}
                    onChange={(e) => setInputs({...inputs, volume: parseFloat(e.target.value) || 0})}
                    required
                  />
                  <select
                    style={selectStyle}
                    id="volume-unit"
                    value={inputs.volumeUnit}
                    onChange={(e) => setInputs({...inputs, volumeUnit: e.target.value})}
                  >
                    <option value="m3">m³</option>
                    <option value="cm3">cm³</option>
                    <option value="mm3">mm³</option>
                    <option value="L">Liter</option>
                    <option value="mL">Milliliter</option>
                    <option value="ft3">ft³</option>
                    <option value="in3">in³</option>
                    <option value="yd3">yd³</option>
                    <option value="gal">gal (US)</option>
                    <option value="qt">quart</option>
                    <option value="pt">pint</option>
                    <option value="km3">km³</option>
                    <option value="mi3">mi³</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={errorMessageStyle}>
              {error}
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
              <button type="button" style={clearBtnStyle} onClick={handleClear}>{t('clearButton')}</button>
            </div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>
              <div style={resultGridStyle}>
                <div style={resultPrimaryStyle}>
                  <span style={resultLabelStyle}>{t('massLabel')}</span>
                  <span style={resultPrimaryValueStyle}>{results.massInKg} kg</span>
                </div>
              </div>

              <div style={conversionTableStyle}>
                <h4 style={conversionTableH4Style}>{t('conversionsTitle')}</h4>
                <div>
                  {Object.entries(massUnits).map(([unit, data]) => (
                    <div key={unit} style={conversionRowStyle}>
                      <span style={conversionUnitStyle}>{data.name} ({unit})</span>
                      <span style={conversionValueStyle}>{results.conversions[unit]}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight1Title')}</strong> {t('insight1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight2Title')}</strong> {t('insight2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight3Title')}</strong> {t('insight3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight4Title')}</strong> {t('insight4Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detail1Title')}</strong> {t('detail1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detail2Title')}</strong> {t('detail2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detail3Title')}</strong> {t('detail3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detail4Title')}</strong> {t('detail4Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={referenceListStyle}>
          <li style={referenceItemStyle}>
            <a
              href="https://fiveable.me/key-terms/calc-ii/mass-calculation"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t('reference1Title')}
            </a>
            <span style={referenceUrlStyle}>fiveable.me</span>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://www.calculator.net/mass-calculator.html"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t('reference2Title')}
            </a>
            <span style={referenceUrlStyle}>calculator.net</span>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://www.curiousjr.com/blogs/what-is-mass"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t('reference3Title')}
            </a>
            <span style={referenceUrlStyle}>curiousjr.com</span>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://study.com/academy/lesson/formula-for-mass-definition-examples-quiz.html"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t('reference4Title')}
            </a>
            <span style={referenceUrlStyle}>study.com</span>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://chem.libretexts.org/Bookshelves/Introductory_Chemistry/Introductory_Chemistry_(CK-12)/02:_Matter_and_Change/2.01:_Matter_Mass_and_Volume"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
            >
              {t('reference5Title')}
            </a>
            <span style={referenceUrlStyle}>chem.libretexts.org</span>
          </li>
        </ul>
      </section>
    </div>
  );
}